import { endOfMonth, isEqual, startOfMonth } from 'date-fns';
import { isEmpty, set, uniqBy } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import {
    AdjustmentSettingInfo,
    ProductBasedAdjustmentSettingInfo,
    Reference
} from 'sr-types/lib/opportunity/v1/graphql';
import { Field, Money } from 'sr-types/lib/search/v1/graphql';
import { CONSTANTS_ID, FETCH_POLICY_CONFIG, PUNCTUATION_MARKS_CONSTANTS } from '../../../common/constants/constants';
import { compareObjects } from '../../../common/helpers/objectProcessing';
import { searchClient, slimResultsQuery } from '../../../common/list/slimQuery';
import { destructureSchedule } from '../../../common/utils/dateTime';
import { isMoney } from '../../../common/utils/money';
import {
    EMPTY_LINE_ITEM,
    LineItem,
    client as opportunityClient,
    preSaveLineItemsQuery
} from '../../opportunity/opportunity';
import { client as productClient, detailsQuery as productDetailsQuery } from './../../product/product';

export const getPreSaveLineItemReference = (objId, activeOrganizationAccount) => {
    return opportunityClient
        .query({
            query: preSaveLineItemsQuery,
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            },
            variables: {
                id: objId
            },
            fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
        })
        .then((res) => {
            return res.data || {};
        });
};

export const getAssociatedStandardProducts = (id, activeOrganizationAccount) => {
    return productClient
        .query({
            query: productDetailsQuery,
            variables: { id: id },
            context: {
                headers: {
                    ownerId: activeOrganizationAccount
                }
            },
            fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
        })
        .then((res) => {
            if (res.data?.product?.baseProduct) {
                return {
                    reference: res.data.product.baseProduct,
                    id: res.data.product.baseProduct.id,
                    label: res.data.product.baseProduct.label
                };
            } else {
                return {};
            }
        });
};

export const getBundleReservableProduct = (lineItems, bundleLineItemId = null) => {
    const bundleId = bundleLineItemId ?? null;
    if (!bundleId) {
        return [];
    }

    const standardProducts = [];
    lineItems?.forEach((li) => {
        // Collect associated items from child line items of the selected bundle
        if (li.parent?.reference?.id === bundleId) {
            standardProducts.push(...(li.associatedItems || []));
            // Collect the product from the selected bundle line item itself
        } else if (li.identity?.id === bundleId && li.product) {
            standardProducts.push(li.product);
        }
    });
    const uniqueReservableProducts = uniqBy(
        standardProducts.filter((product) => product?.reference?.id),
        (product) => product.reference.id
    );
    return uniqueReservableProducts;
};

export const pricingAttributes = ['area(sqFt)', 'area(sqM)'];

export const getPricingAttributesFromLine = async (
    productAttributes,
    costlineAttributes,
    activeOrganizationAccount
): Promise<Field[]> => {
    let pricingAttributesInLine = {};
    productAttributes?.map((a) => {
        if (pricingAttributes.includes(a.name)) {
            pricingAttributesInLine[a.name] = a;
        }
    });

    costlineAttributes?.map((ca) => {
        pricingAttributesInLine = set(pricingAttributesInLine, ca.name, ca);
    });

    const FDs: any = await getPricingAttributes(activeOrganizationAccount);
    const availableAttributes = FDs.data?.results?.hits?.items.map((fd) => {
        return FDtoAttributes(fd);
    });

    const updatedAttributes = availableAttributes.map((attr) => {
        if (pricingAttributesInLine && Object.keys(pricingAttributesInLine).includes(attr.name)) {
            attr = pricingAttributesInLine[attr.name];
        }
        return attr;
    });
    return updatedAttributes;
};

export const getPricingFieldDefinitionsFromLine = (pricingAttributes, fieldDefinitions) => {
    const pricingFieldIds = pricingAttributes?.map((p) => p.fieldDefinitionReference.id);
    const fieldDefinitionsFromLine = fieldDefinitions?.map((fd) => {
        if (pricingFieldIds?.includes(fd.id)) {
            return fd;
        }
    });
    return fieldDefinitionsFromLine?.filter((fd) => fd !== undefined) || [];
};

export const getPricingAttributes = (activeOrganizationAccount) => {
    const nameFilter = pricingAttributes.map((p) => {
        return {
            identifier: 'name',
            value: p
        };
    });

    return new Promise((resolve, reject) => {
        searchClient
            .query({
                query: slimResultsQuery('FieldDefinition'),
                variables: {
                    filters: [
                        {
                            identifier: 'entity',
                            value: 'FieldDefinition'
                        },
                        {
                            identifier: 'entityType',
                            value: 'Product'
                        },
                        ...nameFilter
                    ],
                    page: { size: 1000, from: 0 }
                },
                context: {
                    headers: {
                        ownerId: activeOrganizationAccount
                    }
                },
                fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy
            })
            .then((result) => {
                resolve(result);
            });
    });
};

export const FDtoAttributes = (fieldDefinition): Field => {
    const value = { value: ' ', unit: JSON.parse(fieldDefinition.metadata).unit };
    const attribute = {
        name: fieldDefinition.name,
        fieldType: fieldDefinition.fieldType,
        value: JSON.stringify(value),
        fieldDefinitionReference: fieldDefinition.reference
    };
    return attribute;
};

export const isCompleteMoney = (value: Money) => {
    return value && isMoney(value) && (!isNaN(value.units) || !isNaN(value.nanos));
};

export const getLeaseDuration = (scheduleDateRange) => {
    let leaseDuration = scheduleDateRange?.dateTimeDuration?.value || '';
    if (leaseDuration) {
        const { start, end } = destructureSchedule(scheduleDateRange);
        const endOfTheMonth = endOfMonth(end);
        endOfTheMonth.setHours(0, 0, 0, 0);
        if (
            scheduleDateRange.dateTimeDuration.unit === 'month' &&
            isEqual(start, startOfMonth(start)) &&
            isEqual(end, endOfTheMonth)
        ) {
            leaseDuration = Math.round(scheduleDateRange.dateTimeDuration.value);
        }
    }

    return leaseDuration;
};

const getDiscount = (item) => {
    return {
        name: item?.name || '',
        description: item?.description || '',
        amount: item?.amount,
        percent: item?.percent
    };
};

export const getDefaultDiscountFromSettings = (
    discountSetting: AdjustmentSettingInfo,
    productBasedDiscountSetting: ProductBasedAdjustmentSettingInfo[],
    selectedProductType: string,
    selectedProductSubtype: string
) => {
    let matchingDiscount = getDiscount(discountSetting);
    if (productBasedDiscountSetting && productBasedDiscountSetting.length) {
        const exactMatch = productBasedDiscountSetting.find(
            (setting) => setting.type === selectedProductType && setting.subType === selectedProductSubtype
        );
        if (exactMatch && !isEmpty(exactMatch)) {
            return getDiscount(exactMatch);
        }
        const typeMatch = productBasedDiscountSetting.find(
            (setting) => selectedProductType && setting.type === selectedProductType && setting.subType === ''
        );
        if (typeMatch && !isEmpty(typeMatch)) {
            return getDiscount(typeMatch);
        }
    }
    return matchingDiscount;
};

export const upDateLinesToOpportunityState = (setOpportunityValues, updatedLineItems, modifiedLines) => {
    setOpportunityValues((prev) => {
        return {
            ...prev,
            lineItems: updatedLineItems,
            linesToSave: modifiedLines,
            lineItemsAreUnsaved: true,
            commitPending: true
        };
    });
};

const updateIdentityIds = async (
    addedObjects,
    activeOrganizationAccount,
    scenarioLineIdToReferenceMap: Map<String, Reference>
) => {
    const idMapping = new Map();
    // First loop - concurrently process all items
    await Promise.all(
        addedObjects.map(async (obj) => {
            try {
                const res = await getPreSaveLineItemReference(obj.identity.id, activeOrganizationAccount);
                if (res?.preSaveOpportunityLineItem?.id) {
                    idMapping.set(obj.identity.id, res.preSaveOpportunityLineItem.id);
                    if (scenarioLineIdToReferenceMap.has(obj.identity.id)) {
                        obj.scenarioLineItem = scenarioLineIdToReferenceMap.get(obj.identity.id);
                    }
                    obj.identity = {
                        id: res.preSaveOpportunityLineItem.id,
                        preSave: true
                    };
                }
            } catch (error) {
                console.error('Error fetching pre-save line item reference:', error);
            }
        })
    );
    // Second loop - run after all the first loop's operations are done
    for (const obj of addedObjects) {
        if (obj.parent?.reference?.id && idMapping.has(obj.parent.reference.id)) {
            obj.parent.reference.id = idMapping.get(obj.parent.reference.id);
        }
    }
};

export const updateBookingWithScenarioLines = async (
    scenarioLineItems,
    opportunityVal: any,
    activeOrganizationAccount: string,
    setLineItemsToBeDeleted: Dispatch<SetStateAction<any[]>>,
    updateLineItemState: (lineItemsUpdated: boolean, formUnsaved: boolean) => void,
    resetLocationState: () => void
) => {
    let lineItemsUpdated = false;

    if (scenarioLineItems?.length > 0) {
        const bookingLineItems = opportunityVal.lineItems.filter(
            (lineItem) => lineItem.identity.id !== CONSTANTS_ID.createId
        );

        const bookingFieldsToRetain = (bookingItem: LineItem, scenarioItem: LineItem) => {
            return {
                ...scenarioItem,
                identity: bookingItem.identity,
                createdFrom: bookingItem.createdFrom,
                copyOf: bookingItem.copyOf,
                orderStatus: bookingItem.orderStatus,
                reservation: bookingItem.reservation,
                createReservation: bookingItem.createReservation,
                costLine: bookingItem.costLine
                    ? {
                          ...bookingItem.costLine,
                          ...scenarioItem.costLine,
                          identity: bookingItem.costLine.identity,
                          copyOf: bookingItem.costLine.copyOf,
                          createdFrom: bookingItem.costLine.createdFrom,
                          entityReference: bookingItem.costLine.entityReference
                      }
                    : scenarioItem.costLine || null,
                reference: bookingItem.reference,
                showReservationGuests: bookingItem.showReservationGuests,
                parent: bookingItem.parent || undefined,
                reservationGuestsAvailable: bookingItem.reservationGuestsAvailable || undefined
            };
        };
        const scenarioLineIdToReferenceMap = new Map<String, Reference>();

        // Maintain the order of attributes and associatedItems in scenarioLineItems
        scenarioLineItems.forEach((scenarioLineItem) => {
            scenarioLineIdToReferenceMap.set(scenarioLineItem.identity.id, scenarioLineItem.reference);
            const matchingBookingItem = bookingLineItems.find(
                (bookingItem) => bookingItem.identity.id === scenarioLineItem.createdFrom?.id
            );
            if (matchingBookingItem) {
                // Preserve the order of attributes
                if (Array.isArray(scenarioLineItem.attributes) && Array.isArray(matchingBookingItem.attributes)) {
                    scenarioLineItem.attributes = matchingBookingItem.attributes.map(
                        (attr) => scenarioLineItem.attributes.find((a) => a.name === attr.name) || attr
                    );
                }

                // Preserve the order of associatedItems
                if (
                    Array.isArray(scenarioLineItem.associatedItems) &&
                    Array.isArray(matchingBookingItem.associatedItems)
                ) {
                    scenarioLineItem.associatedItems = matchingBookingItem.associatedItems.map(
                        (assocItem) =>
                            scenarioLineItem.associatedItems.find((a) => a.reference.id === assocItem.reference.id) ||
                            assocItem
                    );
                }
            }
        });
        const fieldsToDelete = [
            'createReservation',
            'createdFrom',
            'copyOf',
            'orderStatus',
            'reservation',
            'costLine.identity',
            'costLine.copyOf',
            'costLine.createdFrom',
            'costLine.entityReference',
            'reference'
        ];
        const { modifiedObjects, deletedObjects, addedObjects, unchangedObjects } = compareObjects(
            scenarioLineItems,
            bookingLineItems,
            (item) => item.createdFrom?.id,
            (item) => item.identity.id,
            bookingFieldsToRetain,
            fieldsToDelete
        );
        [...modifiedObjects, ...addedObjects].forEach((obj) => {
            obj.isModified = true;
        });
        await updateIdentityIds(addedObjects, activeOrganizationAccount, scenarioLineIdToReferenceMap);
        opportunityVal.lineItems = [...modifiedObjects, ...addedObjects, ...unchangedObjects];
        setLineItemsToBeDeleted(deletedObjects);
        if (modifiedObjects.length > 0 || addedObjects.length > 0 || deletedObjects.length > 0) {
            lineItemsUpdated = true;
            updateLineItemState(false, true);
        }
        resetLocationState();
    }
    return lineItemsUpdated;
};

export const generateNameForBooking = (values) => {
    const nameFields = [];
    values.production && values.production.label && nameFields.push(values.production.label);
    values.organization && values.organization.label && nameFields.push(values.organization.label);
    values.primaryItem && values.primaryItem.label && nameFields.push(values.primaryItem.label);
    const generatedName = nameFields.join(` ${PUNCTUATION_MARKS_CONSTANTS.dash} `);
    return generatedName;
};

export const buildLineItemsFromSchedule = (data) => {
    if (!data) return [];

    const reference = data.resource.data.reference;
    return [
        {
            ...EMPTY_LINE_ITEM,
            dateRange: data.dateRange,
            product: {
                label: reference.label,
                reference
            },
            name: reference.label
        }
    ];
};
